// Simple in-memory cache for chat data
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // time to live in milliseconds
}

class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  
  // Default TTL: 5 minutes for chat messages, 10 minutes for companion data
  private defaultTTL = {
    chatMessages: 5 * 60 * 1000,
    companions: 10 * 60 * 1000,
  };

  set<T>(key: string, data: T, ttl?: number): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL.chatMessages,
    };
    this.cache.set(key, entry);
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  invalidate(key: string): void {
    this.cache.delete(key);
  }

  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    });
  }

  clear(): void {
    this.cache.clear();
  }

  // Helper methods for specific data types
  getChatMessages(chatId: string): any | null {
    return this.get(`chat_messages_${chatId}`);
  }

  setChatMessages(chatId: string, data: any) {
    this.set(`chat_messages_${chatId}`, data, this.defaultTTL.chatMessages);
  }

  getCompanions(): any | null {
    return this.get('companions');
  }

  setCompanions(data: any) {
    this.set('companions', data, this.defaultTTL.companions);
  }

  clearCompanions(): void {
    this.invalidate('companions');
  }

  getChatsSummary(): any | null {
    return this.get('chats_summary');
  }

  setChatsSummary(data: any) {
    this.set('chats_summary', data, this.defaultTTL.chatMessages);
  }

  // Clear all chat-related cache (useful on logout)
  clearChatCache(): void {
    this.invalidatePattern('chat_.*');
    this.invalidate('chats_summary');
  }
}

export const cacheService = new CacheService();